import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Check, Database } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ModuleTitleCard, ContainerCard } from "../../components/ui/cards";
import {
    MASTER_MIGRATION_PROCESS,
    MASTER_MIGRATION_TEMPLATE,
    MASTER_BUNDLE_PROCESS,
    MASTER_BUNDLE_TEMPLATE,
} from "../../constants/api";

import { getMasterMigrationBySlug, ensureMasterKeys, masterMigrationTitle } from "./masterMigrationConfigs";
import { migrationBranchFilter } from "./migrationBranch";
import UploadStep from "./steps/UploadStep";
import ReviewStep from "./steps/ReviewStep";
import MigrateStep from "./steps/MigrateStep";

const STEPS = ["Upload", "Review & Fix", "Migrate"];

const Stepper = ({ current }) => (
    <div className="flex items-center gap-2 px-3 pb-2 overflow-x-auto">
        {STEPS.map((label, i) => {
            const done = i < current;
            const active = i === current;
            return (
                <div key={label} className="flex items-center gap-2 shrink-0">
                    <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold border
                            ${done ? "bg-green-600 border-green-600 text-white"
                                : active ? "bg-sidebar-primary border-sidebar-primary text-white"
                                    : "bg-transparent border-gray-300 text-gray-400 dark:border-gray-600"}`}
                    >
                        {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${active ? "text-gray-900 dark:text-white" : "text-muted-foreground"}`}>
                        {label}
                    </span>
                    {i < STEPS.length - 1 && (
                        <span className={`w-8 h-px ${done ? "bg-green-600" : "bg-gray-300 dark:bg-gray-600"}`} />
                    )}
                </div>
            );
        })}
    </div>
);

const MasterMigrationWizard = () => {
    const navigate = useNavigate();
    const { slug, id: routeId } = useParams();
    const location = useLocation();
    const { fetch } = useApiMethods();
    const config = getMasterMigrationBySlug(slug);

    const [step, setStep] = useState(0);
    const [rows, setRows] = useState([]);
    const [migrationId, setMigrationId] = useState(routeId || null);
    const [counts, setCounts] = useState({ processed: 0, conflict: 0 });
    const [options, setOptions] = useState({});
    const [booting, setBooting] = useState(!!routeId);
    const [completedView, setCompletedView] = useState(location.state?.status === "completed");

    const reviewFields = useMemo(
        () => (config?.getReviewFields ? config.getReviewFields(rows, config.fields) : config?.fields),
        [config, rows]
    );

    const pageTitle = masterMigrationTitle(config);
    const processExtra = useMemo(() => {
        const base = config?.isBundle ? {} : { migration_type: config?.type };
        return { ...base, ...migrationBranchFilter() };
    }, [config]);
    const processUrl = config?.isBundle ? MASTER_BUNDLE_PROCESS : MASTER_MIGRATION_PROCESS;
    const templateUrl = config?.isBundle ? MASTER_BUNDLE_TEMPLATE : MASTER_MIGRATION_TEMPLATE;
    const backToList = () => navigate(`/datamigration/master/${slug}`);

    useEffect(() => {
        if (!config?.fetchOptions) {
            setOptions({});
            return;
        }
        if (step !== 1) return;

        let cancelled = false;
        const load = async () => {
            try {
                const loaded = await config.fetchOptions(fetch);
                if (!cancelled) setOptions(loaded || {});
            } catch {
                if (!cancelled) setOptions({});
            }
        };
        load();
        return () => { cancelled = true; };
        // fetch is stable enough to omit; including it retriggers on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, step, config?.type]);

    const bootedRef = useRef(false);
    useEffect(() => {
        if (!routeId || !config || bootedRef.current) return;
        bootedRef.current = true;

        const load = async () => {
            try {
                const res = await fetch(processUrl, {
                    migration_id: routeId,
                    ...(config.isBundle ? {} : { migration_type: config.type }),
                    ...migrationBranchFilter(),
                });
                if (res?.status === "success" && res.data) {
                    const rowData = res.data.row_data || [];
                    const isCompleted =
                        res.data.status === "completed" ||
                        location.state?.status === "completed";

                    if (isCompleted) {
                        setCompletedView(true);
                        setCounts({
                            processed: res.data.processed_records || rowData.length || 0,
                            conflict: 0,
                        });
                        setStep(2);
                    } else {
                        setRows(toUiRows(rowData));
                        setCounts({
                            processed: res.data.processed_records || 0,
                            conflict: res.data.conflict_records || 0,
                        });
                        setStep(1);
                    }
                }
            } catch {
                ToastMsg("error", "Could not load this migration batch");
                backToList();
            } finally {
                setBooting(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId, config]);

    const toUiRows = useCallback(
        (rawRows) =>
            ensureMasterKeys(
                (rawRows || []).map((r) => (config?.deserializeRow ? config.deserializeRow(r) : r))
            ),
        [config]
    );

    const handleParsed = useCallback((parsedRows) => {
        setRows(toUiRows(parsedRows));
        setStep(1);
    }, [toUiRows]);

    const handleProcessed = useCallback((id, nextCounts, conflictRows) => {
        if (id) setMigrationId(id);
        setCounts(nextCounts);
        setRows(toUiRows(conflictRows));
    }, [toUiRows]);

    const handleMigrated = useCallback(() => {
        ToastMsg("success", `${config?.label} migration completed`);
        backToList();
    }, [config]);

    if (!config) {
        return (
            <ContainerCard>
                <p className="text-sm text-muted-foreground p-4">Unknown migration type.</p>
            </ContainerCard>
        );
    }

    if (booting) {
        return (
            <div>
                <ModuleTitleCard Icon={Database} title={pageTitle} />
                <ContainerCard>
                    <div className="h-40 animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg m-4" />
                </ContainerCard>
            </div>
        );
    }

    return (
        <ContainerCard>
            <ModuleTitleCard Icon={Database} title={pageTitle} />

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="pt-4">
                    <Stepper current={step} />
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700" />

                {step === 0 && (
                    <UploadStep
                        options={options}
                        onParsed={handleParsed}
                        onCancel={backToList}
                        fields={config.fields}
                        requiredHeader={config.requiredHeader}
                        headerToKey={config.headerToKey}
                        rowHasErrors={config.rowHasErrors}
                        ensureKeys={ensureMasterKeys}
                        templateUrl={templateUrl}
                        templateQuery={config.isBundle ? {} : { migration_type: config.type }}
                        templateFileType={config.isBundle ? "xlsx" : "csv"}
                        acceptFileTypes={config.isBundle ? ".csv,.xlsx" : ".csv"}
                        stepTitle={`Step 1 — Upload ${config.label.toLowerCase()} data`}
                        stepDescription={config.subtitle}
                    />
                )}
                {step === 1 && (
                    <ReviewStep
                        rows={rows}
                        setRows={setRows}
                        options={options}
                        migrationId={migrationId}
                        counts={counts}
                        onProcessed={handleProcessed}
                        onNext={() => setStep(2)}
                        onCancel={backToList}
                        fields={reviewFields}
                        validateRow={config.validateRow}
                        processUrl={processUrl}
                        processExtra={processExtra}
                        recordsKey="row_data"
                        optionSources={config.optionSources}
                        serializeRow={config.serializeRow}
                    />
                )}
                {step === 2 && (
                    <MigrateStep
                        migrationId={migrationId}
                        counts={counts}
                        onMigrated={handleMigrated}
                        onCancel={backToList}
                        processUrl={processUrl}
                        processExtra={processExtra}
                        entityLabel={config.label.toLowerCase() + " record(s)"}
                        migrateTitle={`Migrate ${config.label.toLowerCase()}?`}
                        showEmployeeNotes={false}
                        completed={completedView}
                    />
                )}
            </div>
        </ContainerCard>
    );
};

export default MasterMigrationWizard;
